import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiX } from 'react-icons/fi';
import { getPaymentMethods, openProCheckout } from '../billingApi';
import { useLanguage } from '../contexts/LanguageContext';
import stripeLight from '../assets/payments/stripe-light.svg';
import stripeDark from '../assets/payments/stripe-dark.svg';
import whishLight from '../assets/payments/whish.svg';
import whishDark from '../assets/payments/whish-dark.svg';
import './PaymentMethodPicker.css';

export default function PaymentMethodPicker({ user, onClose, whishOnly = false }) {
  const { language } = useLanguage();
  const ar = language === 'ar';
  const titleId = useId();
  const dialog = useRef(null);
  const checkoutPending = useRef(false);
  const [methods, setMethods] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const element = dialog.current;
    const previous = document.activeElement;
    if (element.showModal) element.showModal();
    else element.setAttribute('open', '');
    return () => { element.close?.(); previous?.focus(); };
  }, []);

  useEffect(() => {
    let live = true;
    getPaymentMethods(user).then((value) => {
      if (live) setMethods(value);
    }).catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [user, attempt]);

  useEffect(() => {
    const onReturn = (event) => {
      if (!event.persisted) return;
      checkoutPending.current = false;
      setBusy('');
    };
    window.addEventListener('pageshow', onReturn);
    return () => window.removeEventListener('pageshow', onReturn);
  }, []);

  const checkout = async (provider) => {
    if (checkoutPending.current || !methods?.[provider]) return;
    checkoutPending.current = true;
    setBusy(provider);
    setError('');
    try { await openProCheckout(user, provider); }
    catch (e) {
      checkoutPending.current = false;
      setError(e.message);
      setBusy('');
    }
  };
  const hasMethods = methods?.whish || (!whishOnly && methods?.stripe);
  const providerName = busy === 'whish' ? 'Whish' : 'Stripe';
  const heading = busy
    ? (ar ? `جارٍ فتح ${providerName}…` : `Opening ${providerName}…`)
    : (ar ? 'اختر طريقة الدفع' : 'Select your payment method');

  return createPortal(
    <dialog ref={dialog} className="js-payment-picker" aria-labelledby={titleId} dir={ar ? 'rtl' : 'ltr'}
      onCancel={(event) => { event.preventDefault(); if (!busy) onClose(); }}>
      <button type="button" className="js-payment-picker__close" onClick={onClose} disabled={busy}
        aria-label={ar ? 'إغلاق' : 'Close payment options'}><FiX aria-hidden="true" /></button>
      <h2 id={titleId} aria-live="polite">{heading}</h2>
      <div className={`js-payment-picker__methods${busy ? ' is-busy' : ''}`} aria-busy={!methods || Boolean(busy)}>
        {!methods && !error && Array.from({ length: whishOnly ? 1 : 2 }, (_, index) => (
          <div key={index} className="js-payment-picker__skeleton" aria-hidden="true" />
        ))}
        {!whishOnly && methods?.stripe && (
          <button type="button" className={`js-payment-picker__method${busy === 'stripe' ? ' is-selected' : ''}`} aria-label="Stripe"
            disabled={busy} onClick={() => checkout('stripe')}>
            <img className="js-payment-picker__stripe js-payment-picker__logo-dark" src={stripeDark} alt="" />
            <img className="js-payment-picker__stripe js-payment-picker__logo-light" src={stripeLight} alt="" />
            {busy === 'stripe' && <span className="js-payment-picker__spinner" aria-hidden="true" />}
          </button>
        )}
        {methods?.whish && (
          <button type="button" className={`js-payment-picker__method${busy === 'whish' ? ' is-selected' : ''}`} aria-label="Whish Pay"
            disabled={busy} onClick={() => checkout('whish')}>
            <img className="js-payment-picker__whish js-payment-picker__logo-dark" src={whishDark} alt="" />
            <img className="js-payment-picker__whish js-payment-picker__logo-light" src={whishLight} alt="" />
            {busy === 'whish' && <span className="js-payment-picker__spinner" aria-hidden="true" />}
          </button>
        )}
      </div>
      {!methods && !error ? <span className="js-payment-picker__sr-only" role="status">
        {ar ? 'جارٍ تحميل خيارات الدفع…' : 'Loading payment options…'}
      </span> : null}
      {methods && !hasMethods && <p role="alert" className="js-payment-picker__error">
        {ar ? 'طرق الدفع غير متاحة حالياً. حاول مجدداً لاحقاً.' : 'Payment methods are currently unavailable. Please try again later.'}
      </p>}
      {error && <p role="alert" className="js-payment-picker__error">{error}</p>}
      {!methods && error && <button type="button" className="js-payment-picker__retry"
        onClick={() => { setError(''); setAttempt(attempt + 1); }}>{ar ? 'إعادة المحاولة' : 'Try again'}</button>}
    </dialog>, document.body,
  );
}
